import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiErrorMessage } from '@/lib/apiError';
import { AlertCircle, ArrowLeft, Loader2, MailCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Forgot password?" del login (entrega 54): pide el enlace por correo. El
 * servidor contesta lo mismo exista o no la cuenta (backend/services/contrasena.py).
 */
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState('');
  const [error, setError] = useState('');

  const pedir = async (e) => {
    e.preventDefault();
    setEnviando(true);
    setError('');
    try {
      const r = await axios.post(`${API}/auth/forgot-password`, {
        email: email.trim(),
        origin_url: window.location.origin,
      });
      setEnviado(r.data.message);
    } catch (err) {
      setError(apiErrorMessage(err.response?.data, 'Could not send the link. Try again in a few minutes.'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md animate-fade-in" data-testid="forgot-password-page">
        <h1 className="text-3xl font-semibold tracking-tight mb-2">Forgot your password?</h1>
        <p className="text-slate-500 mb-8">
          Enter the email of your account and we&apos;ll send you a link to set a new password.
        </p>

        {enviado ? (
          <div
            role="status"
            className="flex items-start gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800"
            data-testid="forgot-password-sent">
            <MailCheck className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{enviado} Check your spam folder too.</span>
          </div>
        ) : (
          <form onSubmit={pedir} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                required
                className="h-12"
                data-testid="forgot-email-input"
              />
            </div>

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2.5 text-sm text-rose-700"
                data-testid="forgot-password-error">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button type="submit" className="w-full h-12 btn-press" disabled={enviando} data-testid="forgot-password-submit">
              {enviando ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send me the link'}
            </Button>
          </form>
        )}

        <p className="text-center text-slate-500 mt-8">
          <Link to="/login" className="inline-flex items-center gap-1 text-secondary font-medium hover:underline" data-testid="back-to-login">
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
